import { useEffect, useRef, useState } from 'react';

import { Icon, IconName } from '~/modules/elements/akui/icon';
import styles from '~/modules/game-engine/drawing/styles';
import events from '~/modules/game-events/game-events';
import { useEventEffect, useEventListenerSelector } from '~/modules/game-events/hooks';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { waitFinished, waitForReadinessMusic } from '~/modules/sound-manager';
import isE2E from '~/modules/utils/is-e2-e';
import sleep from '~/modules/utils/sleep';
import inputSourceListManager from '~/routes/select-input/input-sources/index';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';

interface Props {
  onFinish: () => void;
  title: string;
  artist: string;
  /**
   * The pause menu is open over this screen. The countdown, the music and the autostart all hold
   * until it closes - without it the song starts behind the menu while the room is reading it.
   */
  paused?: boolean;
}

export const AUTOSTART_TIMEOUT_S = 15;

// A panel is capped rather than simply sharing the row between however many are in it: one or two
// singers on a 65" set stretched to slabs half a screen wide each. Past four the panels wrap to two
// rows of up to four, each a quarter of the row less its share of the gaps.
const PANEL_MAX_WIDTH: Record<number, string> = { 1: '42.5rem', 2: '37.5rem', 3: '32.5rem' };
const panelMaxWidth = (playerCount: number) => PANEL_MAX_WIDTH[playerCount] ?? '30rem';
const PANELS_PER_ROW = 4;

/**
 * The overlay over a loaded song while every phone confirms somebody is holding it.
 *
 * One panel per player rather than a column of rows: on a television this is read from a sofa, and
 * the room needs to see at a glance which singer is still out. Only remote mics are ever waited on -
 * `requestReadiness` resolves immediately on every local input - so a wired or built-in microphone
 * comes up confirmed and is never asked for anything.
 */
function WaitForReadiness({ onFinish, title, artist, paused = false }: Props) {
  const [areAllPlayersReady, setAreAllPlayersReady] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTOSTART_TIMEOUT_S);

  const [confirmedPlayers, setConfirmedPlayers] = useState<string[]>([]);
  useEventEffect(events.readinessConfirmed, (deviceId) => {
    setConfirmedPlayers((current) => [...current, deviceId]);
  });

  // Everything the panels need, read here rather than passed down as `PlayerEntity` objects: the
  // manager mutates those in place, so a panel given one is memoised against an identity that never
  // changes - see the selector in `mic-check`.
  const players = useEventListenerSelector([events.inputListChanged, events.readinessConfirmed], () =>
    PlayersManager.getPlayers().map((player) => {
      const input = inputSourceListManager.getInputForPlayerSelected(player.input, false);
      const isPhone = player.input.source === RemoteMicrophoneInputSource.inputName;
      // `slot-state.ts`'s vocabulary, so a microphone is named the same wherever the room meets it.
      const multiChannel = (input?.channels ?? 1) > 1;

      const name = player.getName();
      // A remote mic arrives labelled with a phone glyph and the singer's own name - see
      // `input-options.ts`. The panel already says who this is in 2.75rem type, so a sub-line
      // repeating it says nothing; what it is for is which microphone they are on.
      const phoneLabel = (input?.label ?? '').replace(/^\p{Extended_Pictographic}+\s*/u, '').trim();

      return {
        deviceId: player.input.deviceId!,
        number: player.number,
        name,
        isPhone,
        inputLabel: isPhone
          ? phoneLabel && phoneLabel !== name
            ? phoneLabel
            : 'Phone'
          : multiChannel
            ? `${input?.label ?? 'Microphone'} · channel ${(input?.channel ?? 0) + 1}`
            : (input?.label ?? 'Built-in microphone'),
      };
    }),
  );

  useEffect(() => {
    if (areAllPlayersReady || paused) return;

    const intervalId = setInterval(() => setSecondsLeft((current) => Math.max(0, current - 1)), 1000);

    return () => clearInterval(intervalId);
  }, [areAllPlayersReady, paused]);

  // The autostart deadline is the number on screen reaching zero rather than a timer of its own,
  // so whatever freezes the countdown freezes the start with it and the two cannot disagree.
  const deadline = useRef<{ promise: Promise<void>; reached: () => void }>(undefined);
  if (!deadline.current) {
    let reached!: () => void;
    const promise = new Promise<void>((resolve) => {
      reached = resolve;
    });
    deadline.current = { promise, reached };
  }
  useEffect(() => {
    if (secondsLeft === 0) deadline.current!.reached();
  }, [secondsLeft]);

  // The sequence below is a chain of awaits rather than a state machine, so pausing it means
  // holding at the next step rather than cancelling anything.
  const isPaused = useRef(paused);
  const resumeWaiters = useRef<Array<() => void>>([]);
  useEffect(() => {
    isPaused.current = paused;
    if (!paused) resumeWaiters.current.splice(0).forEach((resume) => resume());
  }, [paused]);
  const whileRunning = () =>
    isPaused.current ? new Promise<void>((resolve) => resumeWaiters.current.push(resolve)) : Promise.resolve();

  // Only what this screen started is resumed: a pause before the music ever began leaves it that
  // way rather than starting it on the way back out.
  const musicWasPlaying = useRef(false);
  useEffect(() => {
    if (paused) {
      musicWasPlaying.current = waitForReadinessMusic.playing();
      if (musicWasPlaying.current) waitForReadinessMusic.pause();
    } else if (musicWasPlaying.current) {
      musicWasPlaying.current = false;
      void waitForReadinessMusic.play(false);
    }
  }, [paused]);

  useEffect(() => {
    (async () => {
      // can't use `areAllPlayersReady` as it would need to be specified as useEffect dependency
      let allInputsReady = false;
      // The deadline travels with the request, so a phone counts down to the same moment the game
      // does rather than to a constant of its own.
      const inputsReady = PlayersManager.requestReadiness(AUTOSTART_TIMEOUT_S).then(() => {
        allInputsReady = true;
        setAreAllPlayersReady(true);
      });
      const minTimeElapsed = sleep(isE2E() ? 250 : 1_500);

      // Only start the music if waiting for readiness takes some time
      await sleep(250);
      await whileRunning();
      if (!allInputsReady) {
        await waitForReadinessMusic.play(false);
      }

      await Promise.race([Promise.all([inputsReady, minTimeElapsed]), deadline.current!.promise]);
      await whileRunning();
      if (waitForReadinessMusic.playing()) waitFinished.play();
      await sleep(500);
      waitForReadinessMusic.stop();
      await sleep(1000);
      // Checked once more: every phone can confirm while the menu is open, and that path reaches
      // here without ever touching the countdown.
      await whileRunning();
      onFinish();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the readiness/autostart sequence runs once on mount
  }, []);

  const dense = players.length > PANELS_PER_ROW;

  return (
    <div
      className="typography absolute inset-0 z-1000 flex flex-col px-18 pt-16 pb-18"
      style={{
        backgroundColor: 'rgba(0,0,0,.72)',
        // The app's own modal scrim: a dot screen rather than a flat wash, so the song behind it
        // reads as covered rather than dimmed. Sized in `rem` so the dots stay dots on a 4K set.
        backgroundImage: 'radial-gradient(transparent 0.1875rem, rgba(0,0,0,.5) 0.1875rem)',
        backgroundSize: '0.625rem 0.625rem',
      }}>
      <div className="flex items-start justify-between gap-10">
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-active text-[1.625rem] tracking-[0.14em] uppercase">Up next</span>
          <span className="truncate text-[3.5rem] leading-[1.1] font-bold">{title}</span>
          <span className="truncate text-[1.875rem] text-white/80">{artist}</span>
        </div>
        {!areAllPlayersReady && (
          <div className="flex shrink-0 items-center gap-5">
            <span className="text-right text-[1.75rem] leading-[1.3] text-white/80">
              Starting
              <br />
              without them in
            </span>
            <span
              data-test="readiness-countdown"
              className="text-active flex size-33 items-center justify-center rounded-full text-[4rem] font-bold tabular-nums shadow-[inset_0_0_0_0.625rem_rgba(255,165,0,0.9)]">
              {secondsLeft}
            </span>
          </div>
        )}
      </div>

      <div
        className={`mt-13 flex min-h-0 flex-1 justify-center gap-6.5 [view-transition-name:player-mic-check-container] ${
          dense ? 'flex-wrap' : ''
        }`}>
        {players.map((player) => (
          <PlayerPanel
            key={player.number}
            {...player}
            dense={dense}
            maxWidth={panelMaxWidth(players.length)}
            confirmed={confirmedPlayers.includes(player.deviceId) || !player.isPhone}
          />
        ))}
      </div>
    </div>
  );
}

function PlayerPanel({
  number,
  name,
  isPhone,
  inputLabel,
  confirmed,
  maxWidth,
  dense,
}: {
  number: PlayerNumber;
  name: string;
  isPhone: boolean;
  inputLabel: string;
  confirmed: boolean;
  maxWidth: string;
  /** One of two rows: a quarter of the row wide, with smaller type so both rows fit the screen. */
  dense: boolean;
}) {
  const colour = styles.colors.players[number].perfect.fill;
  // No `P{n}` badge, by decision: the name and the colour already say who this is, and the number
  // was noise from across a room.
  const inputIcon: IconName = isPhone ? 'ic:baseline-smartphone' : 'ic:baseline-usb';

  return (
    <div
      className={`ph-no-capture relative flex flex-col items-center justify-center rounded-[1.75rem] px-5 text-center ${
        dense
          ? 'min-h-0 basis-[calc((100%-3*1.625rem)/4)] gap-3 py-4 [@media(max-height:56rem)]:gap-1.5 [@media(max-height:56rem)]:py-2'
          : 'flex-1 gap-6 py-9'
      }`}
      data-test="player-confirm-status"
      data-name={name}
      data-confirmed={confirmed}
      style={
        confirmed
          ? {
              maxWidth,
              // Tinted glass rather than a fill. A row of confirmed panels at full strength was a wall
              // of saturated colour across a television; the colour still says who this is, carried
              // by the badge and the READY label instead of by the whole panel.
              background: `linear-gradient(180deg, color-mix(in srgb, ${colour} 5%, transparent), color-mix(in srgb, ${colour} 24%, transparent))`,
              boxShadow: `inset 0 0 0 0.1875rem color-mix(in srgb, ${colour} 55%, transparent)`,
            }
          : { maxWidth, background: 'rgba(0,0,0,.5)' }
      }>
      {confirmed ? (
        <>
          <span
            aria-hidden
            className={`flex shrink-0 items-center justify-center rounded-full ${
              dense ? 'size-16 [@media(max-height:56rem)]:size-12' : 'size-24'
            }`}
            style={{ background: colour }}>
            {/* Sized in `rem` rather than through `size`, which resolves to raw px: the badge around
                it is `rem` and doubles on a 4K set, and a glyph that stayed put would end up a third
                of the circle it sits in. */}
            <Icon
              icon="ic:baseline-check"
              width={dense ? '2.25rem' : '3.5rem'}
              height={dense ? '2.25rem' : '3.5rem'}
              style={{ color: '#05070b' }}
            />
          </span>
          <span className={`max-w-full truncate font-bold text-white ${dense ? 'text-[2.125rem]' : 'text-[2.75rem]'}`}>
            {name}
          </span>
          {/* The one thing on a confirmed panel still at full strength. It is what carries the
              player's colour now the fill is gone, so it is never muted. */}
          <span className="text-[1.5rem] font-bold tracking-[0.16em] uppercase" style={{ color: colour }}>
            Ready
          </span>
          <span
            className={`flex max-w-full items-center gap-2.5 text-white/50 ${
              dense ? 'text-[1.375rem] [@media(max-height:56rem)]:hidden' : 'text-[1.625rem]'
            }`}>
            <Icon icon={inputIcon} size={7} className="shrink-0" />
            <span className="truncate">{inputLabel}</span>
          </span>
        </>
      ) : (
        <>
          <span aria-hidden className="ak-wait-rim pointer-events-none absolute inset-0 rounded-[1.75rem]" />
          <Icon
            icon="ic:baseline-smartphone"
            size={dense ? 16 : 26}
            className={`text-active ${dense ? '[@media(max-height:56rem)]:hidden' : ''}`}
          />
          <span className={`max-w-full truncate font-bold ${dense ? 'text-[2.125rem]' : 'text-[2.75rem]'}`}>
            {name}
          </span>
          <span
            className={`text-active leading-[1.35] text-pretty ${
              dense ? 'text-[1.375rem] [@media(max-height:56rem)]:leading-tight' : 'text-[1.75rem]'
            }`}>
            Tap Ready
            <br />
            on your phone
          </span>
        </>
      )}
    </div>
  );
}

export default WaitForReadiness;
